import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import { getForecast } from './FinancialService';
import { ForecastResponse, CommitmentForecastItem } from './types';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  needs: '#159b72',
  wants: '#EC4899',
  savings: '#3B82F6',
  red: '#ff454d',
  ink: '#17263d',
  muted: '#78869a',
  wash: '#f4f7fb',
};

const gbp = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(n ?? 0);

const pct = (part: number, total: number) =>
  total > 0 ? Math.round((part / total) * 100) : 0;

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({
  label,
  value,
  sub,
  valueColor,
}: {
  label: string;
  value: string;
  sub?: string;
  valueColor?: string;
}) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700} color={valueColor ?? C.ink} mt={0.5}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Category Card ────────────────────────────────────────────────────────────
function CategoryCard({
  label,
  color,
  budget,
  spentToDate,
  projected,
}: {
  label: string;
  color: string;
  budget: number;
  spentToDate: number;
  projected: number;
}) {
  const isOverProjected = projected > budget;
  const progressPct = Math.min(pct(spentToDate, budget), 100);

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: isOverProjected ? C.red : 'divider',
        height: '100%',
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            {label}
          </Typography>
          {isOverProjected && (
            <Chip label="Over projected" size="small" sx={{ bgcolor: C.red, color: '#fff', fontSize: 10 }} />
          )}
        </Box>

        <Box mb={1.5}>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: `${color}22`,
              '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
            }}
          />
        </Box>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Budget
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {gbp(budget)}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Spent
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {gbp(spentToDate)}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Projected
            </Typography>
            <Typography variant="body2" fontWeight={600} color={isOverProjected ? C.red : 'inherit'}>
              {gbp(projected)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// ─── Commitment Row ───────────────────────────────────────────────────────────
function CommitmentRow({ item }: { item: CommitmentForecastItem }) {
  const bucketColor =
    item.category === 'WANTS' ? C.wants : item.category === 'SAVINGS' ? C.savings : C.needs;
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      py={1}
      borderBottom="1px solid"
      borderColor="divider"
    >
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {item.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Due {item.dueDate}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Chip
          label={item.category}
          size="small"
          sx={{ bgcolor: `${bucketColor}22`, color: bucketColor, fontWeight: 600, fontSize: 10 }}
        />
        <Typography variant="body2" fontWeight={600}>
          {gbp(item.amount)}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Month Selector ───────────────────────────────────────────────────────────
function MonthSelector({
  year,
  month,
  onChange,
}: {
  year: number;
  month: number;
  onChange: (year: number, month: number) => void;
}) {
  const date = new Date(year, month - 1);
  const label = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const now = new Date();
  const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;

  const prev = () => {
    if (month === 1) onChange(year - 1, 12);
    else onChange(year, month - 1);
  };
  const next = () => {
    if (isCurrent) return;
    if (month === 12) onChange(year + 1, 1);
    else onChange(year, month + 1);
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography
        variant="body2"
        sx={{ cursor: 'pointer', userSelect: 'none', px: 1 }}
        onClick={prev}
      >
        ‹
      </Typography>
      <Typography variant="subtitle2" fontWeight={600}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ cursor: isCurrent ? 'default' : 'pointer', userSelect: 'none', px: 1, opacity: isCurrent ? 0.3 : 1 }}
        onClick={next}
      >
        ›
      </Typography>
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const ForecastPage = (): JSX.Element => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<ForecastResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getForecast(year, month)
      .then((r) => setData(r.data))
      .catch(() => setError('Failed to load forecast. Please try again.'))
      .finally(() => setLoading(false));
  }, [year, month]);

  const upcomingCommitments = (data?.commitmentsDueThisMonth ?? []).filter((c) => !c.past);

  return (
    <UserDashboard>
      <Container>
        <Box py={4}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                Forecast
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Projected spend and remaining commitments for this period
              </Typography>
            </Box>
            <MonthSelector
              year={year}
              month={month}
              onChange={(y, m) => { setYear(y); setMonth(m); }}
            />
          </Box>

          {loading && (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          )}

          {error && !loading && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && !data && (
            <Alert severity="info">
              No forecast data available. Make sure your financial profile is set up.
            </Alert>
          )}

          {!loading && data && (
            <>
              {/* KPI Row */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={4}>
                  <KpiCard
                    label="Monthly Income"
                    value={gbp(data.monthlyIncome)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard
                    label="Projected Net"
                    value={gbp(data.projectedNet)}
                    valueColor={data.projectedNet >= 0 ? C.needs : C.red}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard
                    label="Days Remaining"
                    value={`${data.daysRemaining}`}
                    sub={`${data.daysElapsed} elapsed`}
                  />
                </Grid>
              </Grid>

              {/* Category Cards */}
              <Typography variant="h6" fontWeight={700} mb={2}>
                Spend by Category
              </Typography>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={4}>
                  <CategoryCard
                    label="Needs"
                    color={C.needs}
                    budget={data.needsBudget}
                    spentToDate={data.spendToDateNeeds}
                    projected={data.projectedNeeds}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CategoryCard
                    label="Wants"
                    color={C.wants}
                    budget={data.wantsBudget}
                    spentToDate={data.spendToDateWants}
                    projected={data.projectedWants}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CategoryCard
                    label="Savings"
                    color={C.savings}
                    budget={data.savingsBudget}
                    spentToDate={data.spendToDateSavings}
                    projected={data.projectedSavings}
                  />
                </Grid>
              </Grid>

              {/* Commitments this month */}
              {upcomingCommitments.length > 0 && (
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      Upcoming Commitments
                    </Typography>
                    {upcomingCommitments.map((c) => (
                      <CommitmentRow key={c.id} item={c} />
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Assumptions Panel */}
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', bgcolor: C.wash }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>
                    How this is calculated
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    <strong>Income:</strong> Uses your monthly income from your financial profile.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    <strong>Daily rate:</strong> Actual spend to date divided by elapsed days.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    <strong>Projection:</strong> Spend to date + (daily rate × remaining days) + remaining commitments.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    <strong>Commitments:</strong> Active commitments with due dates falling in this month.
                  </Typography>
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      </Container>
    </UserDashboard>
  );
};

export default ForecastPage;
